import { configureStore } from "@reduxjs/toolkit";
import filterReducer, {
  initialState as filterInitialState,
} from "./slices/filterSlice";
import recordReducer from "./slices/recordSlice";
import sessionReducer from "./slices/sessionSlice";

const FILTER_STORAGE_KEY = "filterState";

// Restore persisted filters into the store, merging over defaults so new fields keep their initial value.
export const loadFilterState = () => {
  try {
    const raw = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return undefined;
    const selectedFilter = JSON.parse(raw);
    return {
      filter: {
        ...filterInitialState,
        selectedFilter: {
          ...filterInitialState.selectedFilter,
          ...selectedFilter,
        },
      },
    };
  } catch {
    return undefined;
  }
};

// Drop persisted filters so a different user in the same tab does not inherit them.
export const clearPersistedFilterState = () => {
  try {
    sessionStorage.removeItem(FILTER_STORAGE_KEY);
  } catch {
    // Ignore storage access failures.
  }
};

const store = configureStore({
  reducer: {
    filter: filterReducer,
    record: recordReducer,
    session: sessionReducer,
  },
  preloadedState: loadFilterState(),
});

// Persist the applied filters on every state change so they survive a page refresh.
store.subscribe(() => {
  try {
    sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify(store.getState().filter.selectedFilter),
    );
  } catch {
    // Ignore write failures (e.g. storage full or unavailable).
  }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
